import { useLocalPurchasedOrder } from '@libs/client'
import { getPageBySlug } from '@ui/services'
import { useSetting } from '@ui/hooks'
import * as immutable from 'object-path-immutable'
import { useEffect, useRef, useState } from 'react'

/**
 * Pick the after-sale product for the thankyou page.
 *
 * bvf's cart is a multiple cart (many products, different items/prices), so the
 * after-sale offer must be the product with the LOWEST unit price in the cart
 * that is still sellable. We resolve candidates cheapest-first, fetch each page
 * and keep the first one that still has a sellable variant, then inject its
 * content into the root `pages.*` slots so the after-sale block (which reads the
 * subpath-less setting) renders it.
 */
export function useAfterSaleProduct(): { slug?: string; loading: boolean } {
  const [purchasedOrder] = useLocalPurchasedOrder<Types.Order>()
  const [storeSetting, setStore] = useSetting('store')
  const storeId = storeSetting?.general?.id ?? ''

  const [slug, setSlug] = useState<string | undefined>()
  const [loading, setLoading] = useState<boolean>(!!purchasedOrder?.items?.length)
  const resolvedForOrderRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const orderId = purchasedOrder?.id
    const items = purchasedOrder?.items ?? []

    if (!orderId || !items.length || !storeId) {
      setLoading(false)
      return
    }
    // Only resolve once per purchased order.
    if (resolvedForOrderRef.current === orderId) return
    resolvedForOrderRef.current = orderId

    // Distinct products keyed by slug, keeping the lowest unit price seen.
    const lowestPriceBySlug = new Map<string, number>()
    items.forEach((item) => {
      const itemSlug = item.slug
      if (!itemSlug) return
      const price = typeof item.price === 'number' ? item.price : Number.POSITIVE_INFINITY
      const current = lowestPriceBySlug.get(itemSlug)
      if (current === undefined || price < current) {
        lowestPriceBySlug.set(itemSlug, price)
      }
    })

    const candidates = Array.from(lowestPriceBySlug.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([candidateSlug]) => candidateSlug)

    if (!candidates.length) {
      setLoading(false)
      return
    }

    let cancelled = false
    ;(async function resolve() {
      try {
        setLoading(true)
        for (const candidateSlug of candidates) {
          const page = await getPageBySlug(storeId, candidateSlug)
          const variantItems = page?.setting?.variants?.items ?? {}
          const hasSellable = Object.values(variantItems).some(
            (variant: any) => variant?.sellable ?? true
          )
          if (page?.setting && hasSellable) {
            if (cancelled) return
            setStore((store: S3Types.store) => {
              const _store = immutable.set(store, 'pages.setting', page.setting)
              if (_store?.pages) {
                _store.pages.reviews = page.reviews
                _store.pages.ratings = page.ratings
                _store.pages.mapping_size = page.mapping_size
                _store.pages.mapping_properties = page.mapping_properties
              }
              return _store
            })
            if (!cancelled) setSlug(candidateSlug)
            return
          }
        }
      } catch (error) {
        console.error(`Error resolving after-sale product: ${error}`)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [purchasedOrder?.id, storeId])

  return { slug, loading }
}
